import React, { useState } from "react";
import { isEmptyObject, isNotValue, isValue } from "../../utils/general";
import ContentArea from "../ContentArea/ContentArea";
import "./Style.css";
import CoinGeckoAdapter from "../../utils/Adapters/CoinGeckoAdapter";
import Formatter from "../../utils/Formatter";
import StatusMessage from "../StatusMessage/StatusMessage";
import LineChart, { LineChartDatum } from "../LineChart/LineChart";

type Props = {
  id: string;
};

export const DetailArea = (props: Props) => {
  const [currencyStats, setCurrencyStats] = useState();
  const [currencyPlotData, setCurrencyPlotData] = useState();
  const { id: currencyDetailId } = props;

  if (isNotValue(currencyStats) || isNotValue(currencyPlotData)) {
    loadCurrencyInfo(currencyDetailId, setCurrencyStats, setCurrencyPlotData);
  }

  if (isNotValue(currencyStats) || isNotValue(currencyPlotData)) {
    return renderLoading(currencyDetailId);
  }

  if (isEmptyObject(currencyStats) || isEmptyObject(currencyPlotData) || isValue((currencyPlotData as {error: unknown}).error)) {
    return renderLoadingFailed(currencyDetailId);
  }

  return renderDetail(currencyDetailId, currencyStats, currencyPlotData);
};

const formatDate = (date: Date) =>
  new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(date);

const formatSatValue = (value: number) =>
  `${new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(value)} SAT`;

const formatSatAxisValue = (value: number) => value.toFixed(0);

const Wrapper = (props: { children: React.ReactNode; fullHistoryUrl?: string }) => {
  return (
    <ContentArea
      topRightAction={
        isNotValue(props.fullHistoryUrl)
          ? undefined
          : {
              label: "Full History",
              href: props.fullHistoryUrl,
              target: "_blank",
              rel: "noopener noreferrer",
            }
      }
      isTopArea
    >
      <div className="content-lines">{props.children}</div>
    </ContentArea>
  );
};

const getIntroHeading = (currencyId: string) => {
  if (!currencyId) {
    return "Cryptocurrency Detail in BTC Terms";
  }

  return `${currencyId.toUpperCase()} in BTC Terms`;
};

const renderLoading = (currencyId: string) => {
  return (
    <Wrapper>
      <section className="page-intro">
        <h1>{getIntroHeading(currencyId)}</h1>
        <p>
          Loading market profile, BTC-denominated valuation metrics, and
          historical SAT price trend.
        </p>
      </section>
      <div className="progress-circle">
        <progress className="pure-material-progress-circular" />
      </div>
    </Wrapper>
  );
};

const renderLoadingFailed = (currencyId: string) => {
  return (
    <Wrapper>
      <section className="page-intro">
        <h1>{getIntroHeading(currencyId)}</h1>
        <p>
          This page summarizes a cryptocurrency market profile directly in BTC
          units.
        </p>
      </section>
      <StatusMessage
        title="We could not retrieve data."
        description="Please try again in a minute."
        minHeightRem={24}
      />
    </Wrapper>
  );
};

const renderDetail = (currencyId: string, currencyStats, currencyPlotData) => {
  const {
    name,
    image,
    symbol,
    market_data: marketData,
    categories,
    genesis_date: genesisDate,
  } = currencyStats;

  const btcPrice = marketData.current_price.btc;
  const ath = marketData.ath.btc;
  const btcAthDate = new Date(marketData.ath_date.btc);
  const btcPriceDrop = marketData.ath_change_percentage.btc;
  const btcMarketCap = marketData.market_cap.btc;
  const maxSupply = marketData.max_supply;

  const consensusAlgorithm = Array.isArray(categories)
    ? categories.find((category) =>
        category.toLowerCase().startsWith("proof of"),
      )
    : undefined;

  const now = new Date();
  const fullHistoryUrl = `https://www.coingecko.com/en/coins/${encodeURIComponent(currencyId)}/btc`;
  const symbolUpper = symbol.toUpperCase();
  const currentPriceText = btcPrice ? Formatter.getSatPrice(btcPrice) : "-";
  const athText = ath ? Formatter.getSatPrice(ath) : "-";
  const dropText = isValue(btcPriceDrop)
    ? `${Math.abs(btcPriceDrop).toFixed(1)} %`
    : "-";
  const athReachedText = btcAthDate
    ? `${Formatter.getTime(now.getTime() - btcAthDate.getTime())} ago`
    : "unknown";
  const priceChange24hBtc =
    marketData?.price_change_percentage_24h_in_currency?.btc;
  const priceChange24hText = isValue(priceChange24hBtc)
    ? `${priceChange24hBtc.toFixed(2)} %`
    : "-";
  const marketCapText = btcMarketCap ? Formatter.getBtcPrice(btcMarketCap) : "-";
  const maxSupplyText = maxSupply ? "Known" : "Unknown";
  const consensusText = consensusAlgorithm ?? "Unknown";
  const genesisText = genesisDate ?? "Unknown";
  const detailsStats = [
    { label: "BTC ATH", value: athText },
    { label: "24h Change (BTC)", value: priceChange24hText },
    { label: "Market Cap", value: marketCapText },
    { label: "Max Supply", value: maxSupplyText },
    { label: "Consensus", value: consensusText },
    { label: "Genesis Date", value: genesisText },
  ];

  const { prices } = currencyPlotData;
  const chartData: LineChartDatum[] = prices.map((price) => ({
    date: new Date(price[0]),
    value: Math.round(price[1] * 100000000),
  }));

  return (
    <Wrapper fullHistoryUrl={fullHistoryUrl}>
      <section className="detail-overview-card">
        <div className="detail-overview-top">
          <div className="detail-title-wrap">
            <span className="detail-icon-wrap">
              <img
                src={image.thumb}
                className="detail-title-icon"
                alt={`${name} logo`}
              />
            </span>
            <div>
              <h1 className="detail-title">{name}</h1>
              <div className="detail-symbol">{symbolUpper} / BTC</div>
            </div>
          </div>
          <div className="detail-price-panel">
            <div className="detail-price-panel-label">Current Price</div>
            <div className="detail-price-panel-value">{currentPriceText}</div>
          </div>
        </div>

        <div className="detail-key-metrics">
          <div className="detail-key-metric">
            <div className="detail-key-label">Down from BTC ATH</div>
            <div className="detail-key-value">{dropText}</div>
          </div>
          <div className="detail-key-metric">
            <div className="detail-key-label">ATH Reached</div>
            <div className="detail-key-value">{athReachedText}</div>
          </div>
        </div>

        <div className="detail-stats-grid">
          {detailsStats.map((stat) => (
            <div className="detail-stat-card" key={stat.label}>
              <div className="detail-stat-label">{stat.label}</div>
              <div className="detail-stat-value">{stat.value}</div>
            </div>
          ))}
        </div>
      </section>

      <div className="detail-chart-wrap">
        <LineChart
          data={chartData}
          widthRem={58}
          heightRem={22.4}
          title={`${symbolUpper} / BTC`}
          xAxisLabel="Date"
          yAxisLabel="Price (SAT)"
          valueFormatter={formatSatValue}
          yAxisValueFormatter={formatSatAxisValue}
          dateFormatter={formatDate}
        />
      </div>
      <div className="powered-by">
        Powered by{" "}
        <a
          className="hashrate-link"
          href="https://www.coingecko.com/"
          target="_blank"
          rel="noreferrer"
        >
          CoinGecko
        </a>
      </div>

      {/* <h4>Description</h4>
      <p>{description.en}</p> */}

      {/* <h4>Developer data</h4>
      <p>
        Closed issues in total: <b>{closedIssues}</b>. Commit count in last 4
        weeks: <b>{commitCount4Weeks}</b>. Pull request contributors:{" "}
        <b>{pullRequestContributors}</b>. Subscribers: <b>{subscribers}</b>.
      </p> */}
    </Wrapper>
  );
};

const loadCurrencyInfo = async (
  currencyId,
  setCurrencyStats,
  setCurrencyPlotData,
) => {
  const promises = [
    CoinGeckoAdapter.getCurrencyStats(currencyId),
    CoinGeckoAdapter.getCurrencyPlotData(currencyId),
  ];
  const [stats, plotData] = await Promise.allSettled(promises);

  if (stats.status === "fulfilled") setCurrencyStats(stats.value);
  if (plotData.status === "fulfilled") setCurrencyPlotData(plotData.value);
};
